import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AddProduct.css";
import { createProduct } from "../../../services/Products/Product.service";
import { ProductTypes } from "../../../services/Products/Product.types";
import { toast } from "react-toastify";
import axios from "axios";
import { getAllSellers } from "../../../services/Users/User.service";

// API Response interfaces
interface ApiResponse {
  success: boolean;
  message: string;
  data: any;
}

interface UploadResponse {
  success: boolean;
  message: string;
  data: string;
}

// Metal Type interfaces
interface MetalPurityCaratMaster {
  id: number;
  MetalTypeId: number;
  karat: string | null;
  purity: string | null;
  carat: string | null;
  centValue: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MetalType {
  id: number;
  name: string;
  description: string;
  status: string;
  createdByRole: string;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  metal_purity_carat_masters: MetalPurityCaratMaster[];
}

// Category interface
interface Category {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
  status: string;
  createdByRole: string | null;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  metalTypeId?: number;
  MetalTypeId?: number;
  metal_type_id?: number;
}

// UOM interface
interface UOM {
  id: number;
  name: string;
  description: string;
  status: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

// Article interface
interface Article {
  id: number;
  metalTypeId: number;
  categoryId: number;
  articleName: string;
  description: string | null;
  status: string;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  metalType?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
}

const api = axios.create({
  baseURL: "https://api.sonachandiwale.com/api/v1",
});

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 unauthorized responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("isAuthenticated");
      window.location.href = "/login";
      toast.error("Session expired. Please login again.");
    }
    return Promise.reject(error);
  }
);

const AddProduct: React.FC = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [metalTypes, setMetalTypes] = useState<MetalType[]>([]);
  const [uomList, setUomList] = useState<UOM[]>([]);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  const [productData, setProductData] = useState<Partial<ProductTypes>>({
    description: "",
    categoryId: undefined,
    MetalTypeId: undefined,
    purity: "",
    quantity: undefined,
    wastage: undefined,
    makingCharges: undefined,
  });

  const [thumbnailImage, setThumbnailImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  const [isT2T, setIsT2T] = useState<boolean>(true);
  const [t2tImage, setT2tImage] = useState<File | null>(null);
  const [uploadedT2TImageUrl, setUploadedT2TImageUrl] = useState<string>("");
  const [difference, setDifference] = useState<number>(0);

  // Loading states
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Article dropdown states
  const [selectedArticle, setSelectedArticle] = useState<string>("");
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [openArticle, setOpenArticle] = useState<boolean>(false);
  const [searchArticle, setSearchArticle] = useState<string>("");
  const [showOtherArticle, setShowOtherArticle] = useState<boolean>(false);
  const [newArticleValue, setNewArticleValue] = useState<string>("");

  // Category dropdown states
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [openCategory, setOpenCategory] = useState<boolean>(false);
  const [searchCategory, setSearchCategory] = useState<string>("");
  const [showOtherCategory, setShowOtherCategory] = useState<boolean>(false);
  const [newCategoryValue, setNewCategoryValue] = useState<string>("");

  // Metal Type dropdown states
  const [selectedMetal, setSelectedMetal] = useState<string>("");
  const [selectedMetalId, setSelectedMetalId] = useState<number | null>(null);
  const [selectedMetalData, setSelectedMetalData] = useState<MetalType | null>(null);
  const [openMetal, setOpenMetal] = useState<boolean>(false);
  const [searchMetal, setSearchMetal] = useState<string>("");

  // Purity related states
  const [selectedKarat, setSelectedKarat] = useState<string>("");
  const [selectedCarat, setSelectedCarat] = useState<string>("");
  const [purityValue, setPurityValue] = useState<string>("");
  const [centValue, setCentValue] = useState<string>("");
  const [defaultPurity, setDefaultPurity] = useState<string>("");

  // Available options from metal masters
  const [availableKaratOptions, setAvailableKaratOptions] = useState<{ karat: string, purity: string }[]>([]);
  const [availableCaratOptions, setAvailableCaratOptions] = useState<{ carat: string, cent: number }[]>([]);
  const [availableSilverPurities, setAvailableSilverPurities] = useState<string[]>([]);

  // Weight per gram
  const [weightPerGram, setWeightPerGram] = useState<number | undefined>(undefined);

  // Quantity type selection
  const [quantityType, setQuantityType] = useState<string>("gm");
  const [quantityValue, setQuantityValue] = useState<number | undefined>(undefined);
  const [selectedUomId, setSelectedUomId] = useState<number>(1);
  const [isCreatingCategory, setIsCreatingCategory] = useState<boolean>(false);
  const [isCreatingArticle, setIsCreatingArticle] = useState<boolean>(false);

  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  // For dimensions
  const [dimensions, setDimensions] = useState({
    length: undefined as number | undefined,
    width: undefined as number | undefined,
    height: undefined as number | undefined,
    diameter: undefined as number | undefined
  });

  const [availabilityStatus, setAvailabilityStatus] = useState<string>("Available");
  const [status, setStatus] = useState<string>("pending");

  // Seller dropdown states
  const [sellers, setSellers] = useState<any[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);
  const [selectedSellerName, setSelectedSellerName] = useState<string>("");
  const [openSeller, setOpenSeller] = useState<boolean>(false);
  const [searchSeller, setSearchSeller] = useState<string>("");
  const [loadingSellers, setLoadingSellers] = useState<boolean>(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login first");
      navigate("/login");
      return;
    }
    fetchAllData();
    fetchSellers();
  }, [navigate]);

  // Fetch sellers from API
  const fetchSellers = async () => {
    try {
      setLoadingSellers(true);
      const data = await getAllSellers();
      setSellers(data);
    } catch (error: any) {
      console.error("Error fetching sellers:", error);
      toast.error(error?.response?.data?.message || "Failed to load sellers");
    } finally {
      setLoadingSellers(false);
    }
  };

  // Filter articles when metal or category changes
  useEffect(() => {
    if (selectedMetalId && selectedCategoryId) {
      const filtered = allArticles.filter(
        article => article.metalTypeId === selectedMetalId && article.categoryId === selectedCategoryId
      );
      setFilteredArticles(filtered);
    } else if (selectedMetalId) {
      const filtered = allArticles.filter(article => article.metalTypeId === selectedMetalId);
      setFilteredArticles(filtered);
    } else if (selectedCategoryId) {
      const filtered = allArticles.filter(article => article.categoryId === selectedCategoryId);
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(allArticles);
    }
  }, [selectedMetalId, selectedCategoryId, allArticles]);

  useEffect(() => {
    if (!selectedMetalId) {
      setFilteredCategories(categories);
      return;
    }

    const filtered = categories.filter((category: Category) => {
      const categoryMetalId =
        category.metalTypeId ||
        category.MetalTypeId ||
        category.metal_type_id;

      return String(categoryMetalId) === String(selectedMetalId);
    });

    setFilteredCategories(filtered);
  }, [selectedMetalId, categories]);

  // Update available options when metal changes
  useEffect(() => {
    if (selectedMetalData && selectedMetalData.metal_purity_carat_masters) {
      const masters = selectedMetalData.metal_purity_carat_masters;
      const metalName = selectedMetalData.name.toLowerCase();

      setSelectedKarat("");
      setSelectedCarat("");
      setPurityValue("");
      setCentValue("");
      setDefaultPurity("");

      if (metalName === "gold") {
        const karatOptions = masters
          .filter(m => m.karat !== null && m.purity !== null)
          .map(m => ({
            karat: m.karat as string,
            purity: m.purity as string
          }));
        setAvailableKaratOptions(karatOptions);
        setAvailableCaratOptions([]);
        setAvailableSilverPurities([]);
      } else if (metalName === "diamond") {
        const caratOptions = masters
          .filter(m => m.carat !== null && m.centValue !== null)
          .map(m => ({
            carat: m.carat as string,
            cent: m.centValue as number
          }));
        setAvailableCaratOptions(caratOptions);
        setAvailableKaratOptions([]);
        setAvailableSilverPurities([]);
      } else if (metalName === "silver") {
        const purities = masters
          .filter(m => m.purity !== null)
          .map(m => m.purity as string);
        setAvailableSilverPurities(purities);
        setAvailableKaratOptions([]);
        setAvailableCaratOptions([]);

        if (purities.length > 0) {
          setPurityValue(purities[0]);
        }
      }
    }
  }, [selectedMetalData]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const categoriesResponse = await api.get("/activeCategories");
      if (categoriesResponse.data && categoriesResponse.data.data) {
        setCategories(categoriesResponse.data.data);
      } else if (Array.isArray(categoriesResponse.data)) {
        setCategories(categoriesResponse.data);
      }

      const metalsResponse = await api.get("/metal/Activelist");
      if (metalsResponse.data && metalsResponse.data.data) {
        setMetalTypes(metalsResponse.data.data);
      } else if (Array.isArray(metalsResponse.data)) {
        setMetalTypes(metalsResponse.data);
      }

      // ✅ FETCH UOM LIST - Like Deal Calculator
      const uomResponse = await api.get("/admin/get_All_UOM");
      if (uomResponse.data && uomResponse.data.data) {
        setUomList(uomResponse.data.data);
        // ✅ Set default UOM if available
        if (uomResponse.data.data.length > 0) {
          setSelectedUomId(uomResponse.data.data[0].id);
          setQuantityType(uomResponse.data.data[0].name.toLowerCase());
        }
      } else if (Array.isArray(uomResponse.data)) {
        setUomList(uomResponse.data);
        if (uomResponse.data.length > 0) {
          setSelectedUomId(uomResponse.data[0].id);
          setQuantityType(uomResponse.data[0].name.toLowerCase());
        }
      }

      const articlesResponse = await api.get("/admin/Activearticles");
      if (articlesResponse.data && articlesResponse.data.data) {
        setAllArticles(articlesResponse.data.data);
      } else if (Array.isArray(articlesResponse.data)) {
        setAllArticles(articlesResponse.data);
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
      } else {
        toast.error(error.response?.data?.message || "Failed to load initial data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Image upload function
  const uploadImage = async (file: File, type: 'product' | 't2t'): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await api.post<UploadResponse>("/uplordDoc", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(`${type === 'product' ? 'Product' : 'T2T'} image uploaded successfully`);
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Image upload failed");
      }
    } catch (error: any) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload image");
      throw error;
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbnailImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);

      try {
        setIsUploading(true);
        const imageUrl = await uploadImage(file, 'product');
        setUploadedImageUrl(imageUrl);
      } catch (error) {
        setThumbnailImage(null);
        setPreviewImage(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleT2TImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setT2tImage(file);

    if (file) {
      try {
        setIsUploading(true);
        const imageUrl = await uploadImage(file, 't2t');
        setUploadedT2TImageUrl(imageUrl);
      } catch (error) {
        setT2tImage(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    const numericFields = [
      "wastage", "makingCharges", "thickness", "diameter"
    ];

    if (name === "length" || name === "width" || name === "height" || name === "diameter") {
      setDimensions(prev => ({
        ...prev,
        [name]: value === "" ? undefined : Number(value)
      }));
    } else {
      setProductData((prev) => ({
        ...prev,
        [name]: numericFields.includes(name)
          ? (value === "" ? undefined : Number(value))
          : value,
      }));
    }
  };

  // Gold handlers
  const handleKaratSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKaratValue = e.target.value;
    setSelectedKarat(selectedKaratValue);

    const selected = availableKaratOptions.find(opt => opt.karat === selectedKaratValue);
    if (selected) {
      setPurityValue(selected.purity);
    }
  };

  const handlePurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPurityValue(e.target.value);
  };

  // Diamond handlers
  const handleCaratSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCaratValue = e.target.value;
    setSelectedCarat(selectedCaratValue);

    const selected = availableCaratOptions.find(opt => opt.carat === selectedCaratValue);
    if (selected) {
      setCentValue(selected.cent.toString());
    }
  };

  const handleCentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCentValue(e.target.value);
  };

  // Silver handler
  const handleSilverPurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPurityValue(e.target.value);
  };

  const increaseDifference = () => {
    setDifference((prev) => +(prev + 0.1).toFixed(1));
  };

  const decreaseDifference = () => {
    setDifference((prev) => +(prev - 0.1).toFixed(1));
  };

  const handleQuantityTypeChange = (type: string) => {
    setQuantityType(type);
    const uom = uomList.find(u => u.name.toLowerCase() === type.toLowerCase());
    if (uom) {
      setSelectedUomId(uom.id);
    }
  };

  const handleQuantityValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuantityValue(value === "" ? undefined : Number(value));
  };

  const handleWeightPerGramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWeightPerGram(value === "" ? undefined : Number(value));
  };

  // Helper function to clean object from undefined/null/empty values
  const cleanObject = (obj: Record<string, any>) => {
    const cleaned: Record<string, any> = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined && value !== null && value !== "") {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!selectedMetalId) {
      toast.error("Please select metal type");
      return;
    }

    if (!selectedCategoryId && !selectedCategory) {
      toast.error("Please select category");
      return;
    }

    if (!selectedArticleId && !selectedArticle) {
      toast.error("Please select article");
      return;
    }

    if (!selectedUomId) {
      toast.error("Please select UOM");
      return;
    }

    if (thumbnailImage && !uploadedImageUrl) {
      toast.error("Please wait for product image to upload");
      return;
    }

    if (!isT2T && t2tImage && !uploadedT2TImageUrl) {
      toast.error("Please wait for T2T image to upload");
      return;
    }

    // Optional seller selection warning (non-blocking)
    if (!selectedSellerId) {
      toast.warning("No seller selected. Product will be created without seller association.");
    }

    try {
      setIsSubmitting(true);

      const dimensionsArray = (dimensions.length || dimensions.width || dimensions.height)
        ? [{
          length: dimensions.length ? `${dimensions.length} mm` : "0 mm",
          width: dimensions.width ? `${dimensions.width} mm` : "0 mm",
          height: dimensions.height ? `${dimensions.height} mm` : "0 mm"
        }]
        : [];

      const weightString = weightPerGram && weightPerGram > 0 ? `${weightPerGram}` : "";
      const differenceString = `${difference}gm`;

      const requestBody: Record<string, any> = {
        UomId: selectedUomId,
        description: productData.description || "",
        t2t: isT2T,
        difference: differenceString,
        availabilityStatus: availabilityStatus,
        status: "pending"
      };

      if (selectedMetalId) requestBody.MetalTypeId = selectedMetalId;
      if (selectedMetalData?.name) requestBody.MetalTypeName = selectedMetalData.name;
      if (quantityValue && quantityValue > 0) requestBody.quantity = quantityValue;
      if (weightString) requestBody.weight = weightString;
      if (dimensionsArray.length > 0) requestBody.Dimensions = dimensionsArray;
      if (productData.wastage && productData.wastage > 0) requestBody.wastage = productData.wastage;
      if (productData.makingCharges && productData.makingCharges > 0) requestBody.makingCharges = productData.makingCharges;

      // Image URLs
      if (uploadedImageUrl) requestBody.image = uploadedImageUrl;
      if (uploadedT2TImageUrl) requestBody.tunchImage = uploadedT2TImageUrl;

      // Category
      if (selectedCategoryId) {
        requestBody.categoryId = selectedCategoryId;
      } else if (selectedCategory) {
        requestBody.category_name = selectedCategory;
      }

      // Article
      if (selectedArticleId) {
        requestBody.ArticleId = selectedArticleId;
      } else if (selectedArticle) {
        requestBody.articleName = selectedArticle;
      }

      // Seller ID
      if (selectedSellerId) {
        requestBody.userid = selectedSellerId;
      }

      // Purity fields based on metal type
      if (selectedMetalData) {
        const metalName = selectedMetalData.name.toLowerCase();
        if (metalName === "gold") {
          if (purityValue) requestBody.purity = purityValue;
          if (selectedKarat) requestBody.karat = selectedKarat;
        } else if (metalName === "silver") {
          if (purityValue) requestBody.purity = purityValue;
        } else if (metalName === "diamond") {
          if (selectedCarat) requestBody.carat = selectedCarat;
          if (centValue) requestBody.centValue = Number(centValue);
        } else {
          if (defaultPurity) requestBody.purity = defaultPurity;
        }
      }

      const cleanedBody = cleanObject(requestBody);
      // console.log("Sending request:", cleanedBody);

      await createProduct(cleanedBody);
      toast.success("Your product has been submitted and is pending admin approval.");
      navigate("/product/list");

    } catch (error: any) {
      console.error("API Error:", error);
      const errorMessage = error.response?.data?.message
        || error.message
        || "Failed to create product";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Article handlers
  const handleArticleSelect = (article: Article) => {
    setSelectedArticle(article.articleName);
    setSelectedArticleId(article.id);
    setOpenArticle(false);
    setShowOtherArticle(false);
  };

  const saveOtherArticle = async () => {
    if (!newArticleValue.trim()) {
      toast.error("Please enter article name");
      return;
    }

    if (!selectedMetalId) {
      toast.error("Please select metal type first");
      return;
    }

    if (!selectedCategoryId) {
      toast.error("Please select category first");
      return;
    }

    try {
      setIsCreatingArticle(true);
      const payload = {
        metalTypeId: selectedMetalId,
        categoryId: selectedCategoryId,
        articleName: newArticleValue.trim(),
        description: "",
        status: "ACTIVE",
      };

      const response = await api.post("/admin/article/create", payload);
      const created = response.data?.data || response.data?.article || response.data;

      const newArticle: Article = {
        id: created?.id || Date.now(),
        metalTypeId: created?.metalTypeId || created?.MetalTypeId || selectedMetalId,
        categoryId: created?.categoryId || created?.CategoryId || selectedCategoryId,
        articleName: created?.articleName || newArticleValue.trim(),
        description: created?.description || null,
        status: created?.status || "ACTIVE",
        isActive: created?.isActive ?? true,
        isDelete: created?.isDelete ?? false,
        createdAt: created?.createdAt || new Date().toISOString(),
        updatedAt: created?.updatedAt || new Date().toISOString(),
        metalType: created?.metalType,
        category: created?.category,
      };

      setAllArticles((prev) => {
        const exists = prev.some((item) => item.id === newArticle.id);
        if (exists) return prev;
        return [newArticle, ...prev];
      });

      setFilteredArticles((prev) => {
        const exists = prev.some((item) => item.id === newArticle.id);
        if (exists) return prev;
        return [newArticle, ...prev];
      });

      setSelectedArticle(newArticle.articleName);
      setSelectedArticleId(newArticle.id);
      setOpenArticle(false);
      setShowOtherArticle(false);
      setNewArticleValue("");
      setSearchArticle("");

      toast.success("Article created successfully");
    } catch (error: any) {
      console.error("CREATE ARTICLE ERROR:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "Failed to create article");
    } finally {
      setIsCreatingArticle(false);
    }
  };

  const getFilteredArticlesForDropdown = () => {
    return filteredArticles.filter((a) =>
      a.articleName.toLowerCase().includes(searchArticle.toLowerCase())
    );
  };

  // Category handlers
  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.name);
    setSelectedCategoryId(category.id);
    setProductData(prev => ({
      ...prev,
      categoryId: category.id
    }));
    setOpenCategory(false);
    setShowOtherCategory(false);
  };

  const saveOtherCategory = async () => {
    if (!newCategoryValue.trim()) {
      toast.error("Please enter category name");
      return;
    }

    if (!selectedMetalId) {
      toast.error("Please select metal type first");
      return;
    }

    try {
      setIsCreatingCategory(true);
      const formData = new FormData();
      formData.append("name", newCategoryValue.trim());
      formData.append("status", "ACTIVE");
      formData.append("metalTypeId", String(selectedMetalId));
      formData.append("createdByRole", "admin");

      const response = await api.post("/admin/Create_Category", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const created = response.data?.data || response.data?.category || response.data;

      const newCategory: Category = {
        id: created?.id || Date.now(),
        name: created?.name || newCategoryValue.trim(),
        description: created?.description || null,
        image: created?.image || null,
        status: created?.status || "ACTIVE",
        createdByRole: created?.createdByRole || null,
        isDelete: created?.isDelete || false,
        createdAt: created?.createdAt || new Date().toISOString(),
        updatedAt: created?.updatedAt || new Date().toISOString(),
        metalTypeId: created?.metalTypeId || created?.MetalTypeId || created?.metal_type_id || selectedMetalId,
      };

      setCategories((prev) => {
        const exists = prev.some((item) => item.id === newCategory.id);
        if (exists) return prev;
        return [newCategory, ...prev];
      });

      setFilteredCategories((prev) => {
        const exists = prev.some((item) => item.id === newCategory.id);
        if (exists) return prev;
        return [newCategory, ...prev];
      });

      setSelectedCategory(newCategory.name);
      setSelectedCategoryId(newCategory.id);
      setProductData((prev) => ({ ...prev, categoryId: newCategory.id }));
      setOpenCategory(false);
      setShowOtherCategory(false);
      setNewCategoryValue("");
      setSearchCategory("");

      toast.success("Category created successfully");
    } catch (error: any) {
      console.error("CREATE CATEGORY ERROR:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "Failed to create category");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const filteredCategoriesList = filteredCategories.filter((cat) =>
    cat.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  // Metal Type handlers
  const handleMetalSelect = (metal: MetalType) => {
    setSelectedMetal(metal.name);
    setSelectedMetalId(metal.id);
    setSelectedMetalData(metal);
    setProductData(prev => ({ ...prev, MetalTypeId: metal.id }));
    setOpenMetal(false);
  };

  const filteredMetalsList = metalTypes.filter((metal) =>
    metal.name.toLowerCase().includes(searchMetal.toLowerCase())
  );

  const specialMetals = ["gold", "silver", "diamond"];
  const isSpecialMetal = (metalName: string) =>
    specialMetals.includes(metalName.toLowerCase());

  // Seller dropdown handlers
  const handleSellerSelect = (seller: any) => {
    setSelectedSellerName(`${seller.firstName} ${seller.lastName} (${seller.businessName || seller.email})`);
    setSelectedSellerId(seller.id);
    setOpenSeller(false);
  };

  const filteredSellers = sellers.filter((seller) => {
    const fullName = `${seller.firstName} ${seller.lastName} ${seller.businessName} ${seller.email}`;
    return fullName.toLowerCase().includes(searchSeller.toLowerCase());
  });

  // Render purity fields based on selected metal
  const renderPurityFields = () => {
    if (!selectedMetalData) return null;
    const metalName = selectedMetalData.name.toLowerCase();

    if (metalName === "gold") {
      return (
        <>
          <div className="custom-dropdown-container" style={{ gridColumn: 'span 1' }}>
            <label>Karat *</label>
            <select
              value={selectedKarat}
              onChange={handleKaratSelect}
              className="custom-select"
              required
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select Karat</option>
              {availableKaratOptions.map((option, index) => (
                <option key={`karat-${index}`} value={option.karat}>
                  {option.karat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Purity (%) *</label>
            <input
              type="text"
              value={purityValue}
              onChange={handlePurityChange}
              placeholder="Enter purity %"
              required
            />
          </div>
        </>
      );
    } else if (metalName === "silver") {
      return (
        <div>
          <label>Purity (%) *</label>
          <input
            type="text"
            placeholder="Enter purity %"
            value={purityValue}
            onChange={handleSilverPurityChange}
            required
          />
        </div>
      );
    } else if (metalName === "diamond") {
      return (
        <>
          <div className="custom-dropdown-container" style={{ gridColumn: 'span 1' }}>
            <label>Carat *</label>
            <select
              value={selectedCarat}
              onChange={handleCaratSelect}
              className="custom-select"
              required
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select Carat</option>
              {availableCaratOptions.map((option, index) => (
                <option key={`carat-${index}`} value={option.carat}>
                  {option.carat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Cent Value *</label>
            <input
              type="text"
              value={centValue}
              onChange={handleCentChange}
              placeholder="Enter cent value"
              required
            />
          </div>
        </>
      );
    }

    // For other metals
    return (
      <div style={{ gridColumn: 'span 2' }}>
        <label>Purity/Karat/Cent</label>
        <input
          type="text"
          placeholder="Enter value"
          value={defaultPurity}
          onChange={(e) => setDefaultPurity(e.target.value)}
        />
      </div>
    );
  };

  if (isLoading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="add-product-container">
      <form onSubmit={handleSubmit} className="add-product-form">
        {/* BASIC INFO */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            {/* Metal Type */}
            <div className="custom-dropdown-container">
              <label>Metal Type *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenMetal(!openMetal)}
                >
                  {selectedMetal || "Select Metal Type"}
                </div>
                {openMetal && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search metal type..."
                      value={searchMetal}
                      onChange={(e) => setSearchMetal(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {filteredMetalsList.length > 0 ? (
                        filteredMetalsList.map((metal) => (
                          <div
                            key={metal.id}
                            className="custom-dropdown-option"
                            onClick={() => handleMetalSelect(metal)}
                          >
                            {metal.name}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No metal types found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="custom-dropdown-container">
              <label>Category *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenCategory(!openCategory)}
                >
                  {selectedCategory || "Select Category"}
                </div>
                {openCategory && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search category..."
                      value={searchCategory}
                      onChange={(e) => setSearchCategory(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {filteredCategoriesList.length > 0 ? (
                        filteredCategoriesList.map((cat) => (
                          <div
                            key={cat.id}
                            className="custom-dropdown-option"
                            onClick={() => handleCategorySelect(cat)}
                          >
                            {cat.name}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No categories found
                        </div>
                      )}
                      {!showOtherCategory ? (
                        <div
                          className="custom-dropdown-option other-option"
                          onClick={() => setShowOtherCategory(true)}
                        >
                          + Other
                        </div>
                      ) : (
                        <div className="custom-dropdown-other-input">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new category"
                            className="other-text-input"
                          />
                          <button
                            type="button"
                            onClick={saveOtherCategory}
                            className="other-save-btn"
                            disabled={isCreatingCategory}
                          >
                            {isCreatingCategory ? "Saving..." : "Save"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Article Name */}
            <div className="custom-dropdown-container">
              <label>Article Name *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenArticle(!openArticle)}
                >
                  {selectedArticle || "Select Article"}
                </div>
                {openArticle && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search article..."
                      value={searchArticle}
                      onChange={(e) => setSearchArticle(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {getFilteredArticlesForDropdown().length > 0 ? (
                        getFilteredArticlesForDropdown().map((article) => (
                          <div
                            key={article.id}
                            className="custom-dropdown-option"
                            onClick={() => handleArticleSelect(article)}
                          >
                            {article.articleName}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No articles found
                        </div>
                      )}
                      {!showOtherArticle ? (
                        <div
                          className="custom-dropdown-option other-option"
                          onClick={() => setShowOtherArticle(true)}
                        >
                          + Other
                        </div>
                      ) : (
                        <div className="custom-dropdown-other-input">
                          <input
                            type="text"
                            value={newArticleValue}
                            onChange={(e) => setNewArticleValue(e.target.value)}
                            placeholder="Enter new article"
                            className="other-text-input"
                          />
                          <button
                            type="button"
                            onClick={saveOtherArticle}
                            className="other-save-btn"
                            disabled={isCreatingArticle}
                          >
                            {isCreatingArticle ? "Saving..." : "Save"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Purity fields based on metal selection */}
            {selectedMetal && selectedMetalData && renderPurityFields()}

            {/* SELLER DROPDOWN - placed after purity fields */}
            <div className="custom-dropdown-container" style={{ gridColumn: 'span 2' }}>
              <label>Seller (Optional)</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => !loadingSellers && setOpenSeller(!openSeller)}
                >
                  {selectedSellerName || (loadingSellers ? "Loading sellers..." : "Select Seller")}
                </div>
                {openSeller && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search by name, business, email..."
                      value={searchSeller}
                      onChange={(e) => setSearchSeller(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {filteredSellers.length > 0 ? (
                        filteredSellers.map((seller) => (
                          <div
                            key={seller.id}
                            className="custom-dropdown-option"
                            onClick={() => handleSellerSelect(seller)}
                          >
                            {seller.firstName} {seller.lastName} - {seller.businessName || seller.email}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          {loadingSellers ? "Loading..." : "No sellers found"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Weight Per Article */}
            <div>
              <label>Weight Per Article *</label>
              <input
                type="number"
                value={weightPerGram ?? ""}
                onChange={handleWeightPerGramChange}
                step="0.01"
                placeholder="Enter weight"
              />
            </div>

            {/* EXTRA PURITY INPUT - ONLY FOR NON-SPECIAL METALS */}
            {(!selectedMetal || (selectedMetal && selectedMetalData && !isSpecialMetal(selectedMetal))) && (
              <div>
                <label>Purity%/carat/karat*</label>
                <input
                  type="text"
                  placeholder="Enter purity"
                  value={purityValue}
                  onChange={handleSilverPurityChange}
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* PRODUCT DETAILS */}
        <div className="form-section">
          <h3>Product Details</h3>
          <div className="form-grid">
            {/* Quantity */}
            <div>
              <label>Quantity *</label>
              <input
                type="number"
                placeholder="Enter quantity"
                value={quantityValue ?? ""}
                onChange={handleQuantityValueChange}
                className="quantity-input"
                min="0"
                step="0.001"
                required
              />
              <div className="quantity-cards">
                {uomList.map((uom) => (
                  <button
                    key={uom.id}
                    type="button"
                    className={`quantity-card ${quantityType === uom.name.toLowerCase() ? "active" : ""}`}
                    onClick={() => handleQuantityTypeChange(uom.name.toLowerCase())}
                  >
                    <span className="quantity-label">{uom.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Wastage and Making Charges */}
            {selectedMetalData?.name?.toLowerCase() !== "diamond" ? (
              <>
                <div>
                  <label>Wastage</label>
                  <input
                    type="number"
                    name="wastage"
                    value={productData.wastage ?? ""}
                    onChange={handleChange}
                    step="0.01"
                    placeholder=""
                  />
                </div>
                <div>
                  <label>Making Charges</label>
                  <input
                    type="number"
                    name="makingCharges"
                    value={productData.makingCharges ?? ""}
                    onChange={handleChange}
                    step="0.01"
                    placeholder=""
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label>Making Charges</label>
                  <input
                    type="number"
                    name="makingCharges"
                    value={productData.makingCharges ?? ""}
                    onChange={handleChange}
                    step="0.01"
                    placeholder=""
                  />
                </div>
                <div></div>
              </>
            )}

            {/* T2T Section */}
            <div className="form-section full-width">
              <h3>Tunch to Tunch (T2T)</h3>
              <div className="toggle-buttons">
                <button
                  type="button"
                  className={isT2T ? "active-btn" : ""}
                  onClick={() => setIsT2T(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={!isT2T ? "active-btn" : ""}
                  onClick={() => setIsT2T(false)}
                >
                  No
                </button>
              </div>

              {!isT2T && (
                <div className="t2t-fields-grid">
                  <div className="image-upload-box small">
                    <span>{isUploading ? "Uploading..." : "Upload Tunch Form"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleT2TImageChange}
                      disabled={isUploading}
                    />
                    {uploadedT2TImageUrl && (
                      <small style={{ color: '#10b981', display: 'block', marginTop: '4px' }}>
                        ✓ Uploaded
                      </small>
                    )}
                  </div>
                  <div className="difference-container">
                    <label>Difference (gm)</label>
                    <div className="difference-box">
                      <button type="button" onClick={decreaseDifference}>−</button>
                      <input
                        type="number"
                        step="0.1"
                        value={difference}
                        readOnly
                      />
                      <button type="button" onClick={increaseDifference}>+</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* DIMENSIONS */}
            <div className="form-section full-width">
              <h3>Dimensions</h3>
              <div className="dimensions-grid">
                <div className="dimension-item">
                  <label>Length (mm)</label>
                  <input
                    type="number"
                    name="length"
                    placeholder="Enter length"
                    value={dimensions.length ?? ""}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Width (mm)</label>
                  <input
                    type="number"
                    name="width"
                    placeholder="Enter width"
                    value={dimensions.width ?? ""}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Height (mm)</label>
                  <input
                    type="number"
                    name="height"
                    placeholder="Enter height"
                    value={dimensions.height ?? ""}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Diameter (mm)</label>
                  <input
                    type="number"
                    name="diameter"
                    placeholder="Enter diameter"
                    value={dimensions.diameter ?? ""}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="form-section">
          <h3>Description</h3>
          <div className="description-container">
            <textarea
              name="description"
              placeholder="Enter product description..."
              value={productData.description || ""}
              onChange={handleChange}
              rows={4}
              className="description-textarea"
            />
          </div>
        </div>

        {/* IMAGE */}
        <div className="form-section">
          <h3>Product Image *</h3>
          <div className="image-upload-box">
            {previewImage ? (
              <>
                <img src={previewImage} alt="Preview" className="preview-image" />
                {isUploading && <small style={{ color: '#f59e0b' }}>Uploading...</small>}
                {uploadedImageUrl && !isUploading && (
                  <small style={{ color: '#10b981', display: 'block' }}>✓ Uploaded</small>
                )}
              </>
            ) : (
              <span>{isUploading ? "Uploading..." : "Click to upload image"}</span>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUploading}
              required={!uploadedImageUrl}
            />
          </div>
        </div>

        {/* AVAILABILITY */}
        <div className="form-section">
          <h3>Availability Status</h3>
          <div className="toggle-buttons availability-toggle">
            <button
              type="button"
              className={availabilityStatus === "Available" ? "active-btn" : ""}
              onClick={() => setAvailabilityStatus("Available")}
            >
              Available
            </button>
            <button
              type="button"
              className={availabilityStatus === "Not Available" ? "active-btn" : ""}
              onClick={() => setAvailabilityStatus("Not Available")}
            >
              Not Available
            </button>
          </div>
        </div>

        <div className="button-container">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate("/product/list")}
            disabled={isUploading || isSubmitting}
          >
            Close
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={isUploading || isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;